/**
 * AcousticNoisePage.tsx — Blower noise prediction (BS 4142:2014)
 *
 * Computes LAeq at receptor for the chosen blower compound, including:
 *   - Source level (per blower)
 *   - N+1 unit count
 *   - Distance attenuation (geometric)
 *   - Barrier loss for any acoustic enclosure
 *   - Atmospheric absorption (ISO 9613-1)
 *   - Background L A90 comparison and BS 4142 rating
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Volume2, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted } from '@/lib/sessionPersistence';

const KEY = 'rb_acoustic_noise_v1';

interface Receptor {
  id: string;
  label: string;
  distance_m: number;
  background_LA90: number;
  barrier_dB: number;       // intervening barrier attenuation
}

interface AcousticState {
  blowerSourceLW: number;   // Sound power level dBA at source (each unit)
  numberOfBlowers: number;
  enclosureLoss_dB: number; // building/enclosure attenuation
  receptors: Receptor[];
  daytime: boolean;         // BS 4142 has separate day/night ratings
}

const DEFAULT_STATE: AcousticState = {
  blowerSourceLW: 92,      // typical screw blower in compound
  numberOfBlowers: 3,
  enclosureLoss_dB: 18,
  daytime: true,
  receptors: [
    { id: 'r1', label: 'Nearest residential receptor (NW)', distance_m: 250, background_LA90: 38, barrier_dB: 3 },
    { id: 'r2', label: 'Footpath / public access', distance_m: 80, background_LA90: 42, barrier_dB: 0 },
    { id: 'r3', label: 'Distant settlement (S)', distance_m: 600, background_LA90: 35, barrier_dB: 8 },
  ],
};

// LAeq at receptor from a point source: LAeq = LW - 20·log10(r) - 11 - barrier - enclosure - atm
function predictLAeq(s: AcousticState, r: Receptor): number {
  const N = s.numberOfBlowers;
  const totalLW = s.blowerSourceLW + 10 * Math.log10(N);
  const distanceLoss = 20 * Math.log10(Math.max(1, r.distance_m)) + 11;
  // Atmospheric absorption (ISO 9613-1 simplified for 500 Hz, temperate UK)
  const atm = 0.005 * r.distance_m;
  return totalLW - distanceLoss - s.enclosureLoss_dB - r.barrier_dB - atm;
}

function bs4142Rating(specific: number, background: number, daytime: boolean): { rating: number; verdict: string; colour: string } {
  // BS 4142:2014 simplified: rating = specific + character correction (0–6 dB)
  // Daytime / night threshold typically 5 dB above background = "marginal", 10 dB = "significant"
  const charCorrection = 3; // assume blower has tonal/impulsive component
  const rating = specific + charCorrection;
  const excess = rating - background;
  let verdict: string;
  let colour: string;
  const sigThresh = daytime ? 10 : 5;
  const margThresh = daytime ? 5 : 0;
  if (excess >= sigThresh) { verdict = 'Significant impact likely (BS 4142)'; colour = 'border-red-700 bg-red-900/20 text-red-300'; }
  else if (excess >= margThresh) { verdict = 'Adverse impact dependent on context'; colour = 'border-amber-700 bg-amber-900/20 text-amber-300'; }
  else if (excess >= -5) { verdict = 'Low impact'; colour = 'border-emerald-700 bg-emerald-900/20 text-emerald-300'; }
  else { verdict = 'Inaudible against background'; colour = 'border-emerald-700 bg-emerald-900/20 text-emerald-300'; }
  return { rating, verdict, colour };
}

function fmt(n: number, d = 1) { if (!isFinite(n)) return '—'; return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }); }

function uid() { return 'r' + Math.random().toString(36).slice(2, 8); }

export default function AcousticNoisePage() {
  const navigate = useNavigate();
  const [s, setS] = useState<AcousticState>(() => loadPersisted<AcousticState>(KEY, DEFAULT_STATE));

  useEffect(() => { savePersisted(KEY, s); }, [s]);

  const results = useMemo(() => s.receptors.map(r => {
    const specific = predictLAeq(s, r);
    const rating = bs4142Rating(specific, r.background_LA90, s.daytime);
    return { r, specific, ...rating };
  }), [s]);

  const updateR = (id: string, patch: Partial<Receptor>) =>
    setS({ ...s, receptors: s.receptors.map(r => r.id === id ? { ...r, ...patch } : r) });
  const addR = () => setS({ ...s, receptors: [...s.receptors, { id: uid(), label: 'New receptor', distance_m: 100, background_LA90: 40, barrier_dB: 0 }] });
  const removeR = (id: string) => setS({ ...s, receptors: s.receptors.filter(r => r.id !== id) });

  const worst = results.reduce((a, b) => b.rating > a.rating ? b : a, results[0]);

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Volume2}
        title="Acoustic — Blower Noise Prediction"
        subtitle="BS 4142:2014 specific LAeq, background LA90 and rating margin at noise-sensitive receptors"
        actions={
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      {/* Worst-case banner */}
      {worst && (
        <div className={`rounded-xl border-2 p-4 ${worst.colour}`}>
          <div className="text-xs uppercase tracking-wider opacity-80">Worst-case receptor</div>
          <div className="text-lg font-bold text-slate-100 mt-0.5">{worst.r.label}</div>
          <div className="text-xs text-slate-300 mt-1">
            Specific LAeq <span className="font-bold">{fmt(worst.specific, 1)} dB</span> ·
            Background LA90 <span className="font-bold">{worst.r.background_LA90} dB</span> ·
            Rating <span className="font-bold">{fmt(worst.rating, 1)} dB</span> ·
            <span className="ml-1">{worst.verdict}</span>
          </div>
        </div>
      )}

      {/* Inputs */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-3">Source & enclosure</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Sound power per blower (dBA)</label>
            <input type="number" value={s.blowerSourceLW}
              onChange={e => setS({ ...s, blowerSourceLW: Number(e.target.value) })}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Number of blowers (N+1)</label>
            <input type="number" value={s.numberOfBlowers}
              onChange={e => setS({ ...s, numberOfBlowers: Number(e.target.value) })}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Enclosure / building loss (dB)</label>
            <input type="number" value={s.enclosureLoss_dB}
              onChange={e => setS({ ...s, enclosureLoss_dB: Number(e.target.value) })}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Period</label>
            <select value={s.daytime ? 'day' : 'night'}
              onChange={e => setS({ ...s, daytime: e.target.value === 'day' })}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100">
              <option value="day">Daytime (07:00–23:00)</option>
              <option value="night">Night-time (23:00–07:00)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Receptor table */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-100">Noise-sensitive receptors</div>
          <button onClick={addR} className="text-xs text-slate-300 hover:text-slate-100">+ Add</button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-2 py-1.5 text-left text-slate-300">Receptor</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Dist (m)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Background LA90</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Barrier (dB)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Specific LAeq</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Rating</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Margin</th>
                <th className="px-2 py-1.5 text-left text-slate-300">Verdict</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {results.map(({ r, specific, rating, verdict, colour }) => (
                <tr key={r.id} className={`border-b border-slate-800 ${colour}`}>
                  <td className="px-2 py-1">
                    <input value={r.label} onChange={e => updateR(r.id, { label: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100" />
                  </td>
                  <td className="px-2 py-1 text-right">
                    <input type="number" value={r.distance_m}
                      onChange={e => updateR(r.id, { distance_m: Number(e.target.value) })}
                      className="w-20 bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100 text-right" />
                  </td>
                  <td className="px-2 py-1 text-right">
                    <input type="number" value={r.background_LA90}
                      onChange={e => updateR(r.id, { background_LA90: Number(e.target.value) })}
                      className="w-16 bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100 text-right" />
                  </td>
                  <td className="px-2 py-1 text-right">
                    <input type="number" value={r.barrier_dB}
                      onChange={e => updateR(r.id, { barrier_dB: Number(e.target.value) })}
                      className="w-16 bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100 text-right" />
                  </td>
                  <td className="px-2 py-1 text-right text-slate-100 tabular-nums">{fmt(specific, 1)}</td>
                  <td className="px-2 py-1 text-right text-slate-100 tabular-nums font-bold">{fmt(rating, 1)}</td>
                  <td className="px-2 py-1 text-right text-slate-100 tabular-nums">{fmt(rating - r.background_LA90, 1)}</td>
                  <td className="px-2 py-1 text-xs">{verdict}</td>
                  <td className="px-2 py-1">
                    <button onClick={() => removeR(r.id)} className="text-slate-500 hover:text-red-400 text-xs">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> Methodology
        </div>
        <div className="text-xs text-slate-400 leading-relaxed">
          Predicted specific LAeq at each receptor uses a point-source attenuation model with N+1 unit count,
          geometric spreading (20·log10(r) + 11), enclosure loss, barrier attenuation and atmospheric absorption.
          A 3 dB character correction is added per BS 4142:2014 for typical blower tonal/impulsive content; this
          should be replaced with site-measured spectra during the acoustic baseline survey. Background LA90
          values are placeholders pending the baseline survey at each receptor.
        </div>
      </div>
    </div>
  );
}
